// ActiveSubscriptionSummary.jsx
import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { ModernButton } from "../components/DefaultImports";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { Card } from "react-bootstrap";
import { ConfirmationModal } from "./ConfirmationModal";
import { planTier } from "../utils/planFeatures";
import { displayPlanName as sharedDisplayPlanName } from "../utils/planNames";
import { resumeSubscription, cancelSubscription } from "../actions/paymentActions";
import { fetchRestaurantDetails } from "../actions/restaurantActions";
import { SectionTitle } from "./DefaultImports";

/* Palette and helper for plan colors */
const palette = ["#9FBBC1", "#EE754C", "#99B0AD"];
const getPlanColor = n => palette[(Number(n) - 1) % palette.length];

/* ---------- Styled components ---------- */
const Wrapper = styled.div``;
const PlanName = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: ${({
  color
}) => color || "#333"};
  margin: 8px 0 6px;
`;
const PlanDescription = styled.p`
  font-size: 14px;
  color: #444;
  margin: 0 0 16px;
`;
const DiscountNote = styled.div`
  font-size: 13px;
  color: #28a745;
  font-style: italic;
  margin: 0 0 12px;
`;
const DowngradeWarning = styled.div`
  margin: 12px 0 18px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;

  strong {
    color: #6f5100;
  }
`;
const DowngradeWarningTitle = styled.div`
  font-weight: 800;
  margin-bottom: 4px;
`;
const RenewalLine = styled.p`
  margin: 0 0 14px;
  color: #6f7a79;
  font-size: 14px;
  line-height: 1.5;

  strong {
    color: #4b3335;
    font-weight: 600;
  }
`;
const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 8px 0 12px;

  li {
    display: flex;
    /* start, not center: a wrapped two-line feature must keep its tick
       against the first line rather than floating to the middle. */
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;

    svg {
      font-size: 14px;
      /* Optical centring on the label's cap height. */
      margin-top: 3px;
      flex: 0 0 auto;
      color: ${({
  iconColor
}) => iconColor || "#ee754c"};
    }
  }
`;
const FeatureText = styled.div`
  min-width: 0;
`;
const FeatureTitle = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4b3335;
  line-height: 1.4;
`;
const FeatureDesc = styled.span`
  display: block;
  font-size: 13px;
  color: #6f7a79;
  line-height: 1.45;
  margin-top: 2px;
`;
const ViewMore = styled.button`
  background: none;
  border: none;
  color: ${p => p.color || "#ee754c"};
  cursor: pointer;
  font-size: 14px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;
const Buttons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0eae6;
`;

const actionBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;

  &:focus-visible {
    outline: 2px solid #ee754c;
    outline-offset: 2px;
  }
`;

const PrimaryLink = styled(Link)`
  ${actionBase}
  background: #ee754c;
  border: 1px solid #ee754c;
  color: #ffffff;

  &:hover {
    background: #e05f33;
    border-color: #e05f33;
    color: #ffffff;
  }
`;

/* Deactivating is destructive and rare, so it stays quiet until you reach
   for it - grey by default, red only on hover. */
const DangerAction = styled.button`
  ${actionBase}
  background: transparent;
  border: 1px solid #e3dcd8;
  color: #6f7a79;

  &:hover {
    background: #fff5f2;
    border-color: #f0b4a0;
    color: #d0451b;
  }
`;

const SecondaryAction = styled.button`
  ${actionBase}
  background: transparent;
  border: 1px solid #cfdcda;
  color: #5b706d;

  &:hover {
    background: #f2f7f6;
    border-color: #9fbbc1;
    color: #3f5b57;
  }
`;
const EmptyTitle = styled.h4`
  font-size: 20px;
  font-weight: 800;
  color: #99b0ad;
  text-align: center;
  margin: 4px 0 6px;
`;
const EmptyText = styled.p`
  text-align: center;
  color: #6f7a79;
  margin: 0 0 10px;
`;
const PlanCard = styled(Card)`
  border-radius: 20px;
  border: 1px solid #dee2e6;
  background-color: #ffffff;
  padding: 32px 24px;
  text-align: center;
  transition: transform 0.3s ease;
  min-height: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-5px);
  }
`;

/* ---------- helpers ---------- */
// The 4/5/6 -> 1/2/3 collapse lives in utils/planFeatures now, so the billing
// screens and the feature gates cannot drift apart.
const normalizeSubLevel = planTier;

/* Offers are one string with ";" between features - and the features
   themselves contain commas ("pode ser desligada, deixando as paginas..."),
   so splitting on "," tore sentences in half and glued unrelated ones
   together. Mirrors splitOffersSafely in ChoosePlanAndPay. */
const splitOffers = value =>
  String(value || "")
    .split(/\s*;\s*/g)
    .map(item => item.trim())
    .filter(Boolean);

/* "Marca Soup: pode ser desligada" -> a bold label and its detail. */
const splitTitleAndDesc = item => {
  const idx = item.indexOf(":");
  if (idx === -1) return { title: item.trim(), desc: "" };
  return { title: item.slice(0, idx).trim(), desc: item.slice(idx + 1).trim() };
};

/* Tier 1 is sold as a free plan with a monthly reservation cap, so it is
   named for what it is rather than by the package row's "Essencial". Covers
   package 1 and its yearly twin 4. */
const displayPlanName = (pkg, t) =>
  sharedDisplayPlanName(pkg, t) || t("ui.i18nFinalCleanup.plan");
const toDate = v => {
  if (!v) return null;

  // aceita Date, ISO string ou timestamp
  if (v instanceof Date) return v;
  const n = Number(v);
  if (Number.isFinite(n)) {
    return new Date(n < 1e12 ? n * 1000 : n); // sec ou ms
  }
  return new Date(v); // ISO do Django
};
const fmt = (d, locale) => d ? d.toLocaleDateString(locale, {
  year: "numeric",
  month: "long",
  day: "numeric"
}) : "—";

/**
 * Props:
 * - onChangePlan?: () => void
 * - onCancelPlan?: () => void
 * - onSubscribe?: () => void
 * - restaurant_id: string | number
 */
export default function ActiveSubscriptionSummary({
  onChangePlan,
  onCancelPlan,
  onSubscribe,
  restaurant_id
}) {
  const { t, i18n } = useTranslation();
  const appLocale = (i18n.resolvedLanguage || i18n.language || "en").startsWith("pt") ? "pt-PT" : (i18n.resolvedLanguage || i18n.language || "en").startsWith("es") ? "es-ES" : "en-GB";
  const dispatch = useDispatch();
  const params = useParams();
  const [showMore, setShowMore] = useState(false);
  const [subscription, setSubscription] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'cancel' | 'resume'
  const [working, setWorking] = useState(false);
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurantDetails;
  const {
    getSubscriptionPackages
  } = useSelector(state => state.subscriptionPackagesStore);
  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
  }, [dispatch]);
  useEffect(() => {
    if (!getSubscriptionPackages || !restaurants) return;
    // Match the package the restaurant actually bought before falling back to
    // its tier: sub_lvl 6 is yearly Completo, and collapsing it to 3 first
    // would show this card the monthly package - wrong price, wrong cadence.
    const raw = Number(restaurants.sub_lvl || 0);
    const tier = planTier(raw);
    const byNumber = number =>
      getSubscriptionPackages.find(
        pkg => Number(pkg.subscription_number) === Number(number)
      );
    const match = byNumber(raw) || byNumber(tier);
    if (match) setSubscription(match);
  }, [getSubscriptionPackages, restaurants]);

  // Sem plano
  if (restaurants?.subscription_status === "incomplete" || restaurants?.subscription_status === "incomplete_expired") {
    return <Wrapper>
        <SectionTitle>{t("ui.components.activeSubscription.planoAtivo")}</SectionTitle>

        <EmptyTitle>{t("ui.components.activeSubscription.semPlanoAtivo")}</EmptyTitle>

        <EmptyText>{t("ui.components.activeSubscription.naoEncontramosNenhumPlanoAtivoNeste")}</EmptyText>

        <Buttons>
          <Link to={`/pick_plan_and_pay/${restaurant_id}`}>
            <ModernButton color="#de8c3a" fontSize="14px" onClick={onSubscribe}>{t("ui.components.activeSubscription.aderirAPlano")}</ModernButton>
          </Link>
        </Buttons>
      </Wrapper>;
  }

  /* ---- Estado + Datas ---- */
  const status = restaurants?.subscription_status; // 'active' | 'trialing' | 'paused' | ...
  const cancelAtPeriodEnd = !!restaurants?.cancel_at_period_end;
  const periodEnd = toDate(restaurants?.subscription_end_date || restaurants?.current_period_end || restaurants?.subscription_current_period_end);
  const periodEndLabel = fmt(periodEnd, appLocale);
  const downgradeRawLevel = Number(restaurants?.downgrade_at_period_end || 0);
  const downgradeAtPeriodEnd = normalizeSubLevel(downgradeRawLevel);
  const hasScheduledDowngrade = downgradeAtPeriodEnd > 0;
  const downgradeTargetSubscription = hasScheduledDowngrade
    ? getSubscriptionPackages?.find(
        pkg => Number(pkg.subscription_number) === downgradeRawLevel
      ) ||
      getSubscriptionPackages?.find(
        pkg => Number(pkg.subscription_number) === downgradeAtPeriodEnd
      )
    : null;
  const downgradeTargetName = downgradeTargetSubscription
    ? displayPlanName(downgradeTargetSubscription, t)
    : t("ui.i18nFinalCleanup.planNumber", { number: downgradeAtPeriodEnd });
  const isPaused = status === "paused" || cancelAtPeriodEnd === true;
  const color = getPlanColor(subscription.subscription_number);
  const isYearly = Number(subscription.number_of_days) === 365;
  const offers = splitOffers(subscription.offers);

  // Ações
  const askCancel = () => {
    setConfirmType("cancel");
    setConfirmOpen(true);
  };
  const askResume = () => {
    setConfirmType("resume");
    setConfirmOpen(true);
  };
  const onConfirm = async () => {
    if (working) return;
    try {
      setWorking(true);
      if (confirmType === "cancel") {
        await dispatch(cancelSubscription({
          restaurant_id
        }));
        if (typeof onCancelPlan === "function") {
          onCancelPlan();
        }
      } else if (confirmType === "resume") {
        await dispatch(resumeSubscription({
          restaurant_id
        }));
        if (typeof onChangePlan === "function") {
          onChangePlan();
        }
      }
      await dispatch(fetchRestaurantDetails(restaurant_id));
    } finally {
      setWorking(false);
      setConfirmOpen(false);
      setConfirmType(null);
    }
  };
  return <Wrapper>
      <SectionTitle>{t("ui.components.activeSubscription.planoAtivo2")}</SectionTitle>

      {/* One sentence per key. It used to be assembled from four fragments
          ("O teu plano" + "renova" + "a" + date), none of which carried the
          spaces between them, so it rendered as "O teu planorenovaa 4 de...". */}
      <RenewalLine>
        <Trans
          i18nKey={
            isPaused
              ? "ui.components.activeSubscription.planEndsOn"
              : "ui.components.activeSubscription.planRenewsOn"
          }
          values={{ date: periodEndLabel }}
          components={{ b: <strong /> }}
        />
      </RenewalLine>

      {hasScheduledDowngrade && <DowngradeWarning>
          <DowngradeWarningTitle>{t("ui.components.activeSubscription.alteracaoDePlanoAgendada")}</DowngradeWarningTitle>
          <Trans
            i18nKey="ui.components.activeSubscription.scheduledChangeBody"
            values={{ plan: downgradeTargetName, date: periodEndLabel }}
            components={{ b: <strong /> }}
          />
        </DowngradeWarning>}

      <PlanName color={color}>{displayPlanName(subscription, t)}</PlanName>

      <PlanDescription>
        {subscription.description || t("ui.i18nFinalCleanup.activePlanDescription", { cadence: isYearly ? t("ui.i18nFinalCleanup.yearly") : t("ui.i18nFinalCleanup.monthly"), status: isPaused ? t("ui.i18nFinalCleanup.paused") : t("ui.i18nFinalCleanup.active") })}
      </PlanDescription>

      {isYearly && <DiscountNote>{t("ui.components.activeSubscription.inclui20DeDescontoEmRelacao")}</DiscountNote>}

      {offers.length > 0 && <>
          <FeatureList iconColor={color}>
            {(showMore ? offers : offers.slice(0, 2)).map((offer, idx) => {
          const { title, desc } = splitTitleAndDesc(offer);
          return <li key={idx}>
                  <FontAwesomeIcon icon={faCircleCheck} />
                  <FeatureText>
                    <FeatureTitle>{title}</FeatureTitle>
                    {desc ? <FeatureDesc>{desc}</FeatureDesc> : null}
                  </FeatureText>
                </li>;
        })}
          </FeatureList>

          {offers.length > 2 && <ViewMore color={color} onClick={() => setShowMore(s => !s)}>
              {showMore ? t("ui.screens.choosePlanAndPay.verMenos") : t("ui.screens.choosePlanAndPay.verMais2")}
            </ViewMore>}
        </>}

      {/* One primary action, and a quiet one beside it. Two solid buttons of
          equal weight made cancelling look like the thing to do. */}
      <Buttons>
        {isPaused ? <SecondaryAction type="button" onClick={askResume}>
            {t("ui.components.activeSubscription.reativarPlano")}
          </SecondaryAction> : <DangerAction type="button" onClick={askCancel}>
            {t("ui.components.activeSubscription.desativarPlano")}
          </DangerAction>}

        <PrimaryLink to={`/pick_plan_and_pay/${restaurant_id}`} onClick={onChangePlan}>
          {t("ui.components.activeSubscription.mudarPlano")}
        </PrimaryLink>
      </Buttons>

      <ConfirmationModal show={confirmOpen} onHide={() => !working && setConfirmOpen(false)} onConfirm={onConfirm} title={confirmType === "cancel" ? t("ui.components.activeSubscription.desativarPlano") : t("ui.components.activeSubscription.reativarPlano")} body={confirmType === "cancel" ? t("ui.i18nFinalCleanup.cancelPlanWarning", { date: periodEndLabel }) : t("ui.i18nCleanup2.oTeuPlanoEstaAtualmenteInativoPretendesReativar")} confirmLabel={working ? t("ui.i18nCleanup2.aProcessar") : confirmType === "cancel" ? t("ui.components.activeSubscription.desativarPlano") : t("ui.components.activeSubscription.reativarPlano")} cancelLabel={working ? t("ui.i18nFinalCleanup.pleaseWait") : t("common.cancel")} confirmColor={confirmType === "cancel" ? "#ff704d" : "#9FBBC1"} cancelColor="#99b0ad" onCancel={() => !working && setConfirmOpen(false)} centered />
    </Wrapper>;
}
